import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, XCircle, Loader2, ArrowLeft, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'

// API base URL with production fallback
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

interface Preferences {
  product_updates: boolean
  feedback_requests: boolean
  promotions: boolean
  newsletter: boolean
  unsubscribed_all: boolean
}

export default function EmailPreferencesPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [preferences, setPreferences] = useState<Preferences | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid link. Please log in to manage your preferences.')
      return
    }

    const loadPreferences = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/email-preferences/via-token?token=${encodeURIComponent(token)}`,
        )
        const data = await response.json()

        if (response.ok && data.success) {
          setPreferences(data.preferences)
          setStatus('loaded')
        } else {
          setStatus('error')
          setMessage(data.detail || 'Failed to load preferences. The link may have expired.')
        }
      } catch {
        setStatus('error')
        setMessage('An error occurred. Please try again or log in.')
      }
    }

    loadPreferences()
  }, [token])

  const handleToggle = (key: keyof Preferences) => {
    if (!preferences) return
    setPreferences({ ...preferences, [key]: !preferences[key] })
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!token || !preferences) return
    setSaving(true)

    try {
      const response = await fetch(
        `${API_BASE_URL}/email-preferences/via-token?token=${encodeURIComponent(token)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_updates: preferences.product_updates,
            feedback_requests: preferences.feedback_requests,
            promotions: preferences.promotions,
            newsletter: preferences.newsletter,
          }),
        },
      )
      const data = await response.json()

      if (response.ok && data.success) {
        setPreferences(data.preferences)
        setHasChanges(false)
        toast.success('Preferences saved successfully')
      } else {
        toast.error(data.detail || 'Failed to save preferences')
      }
    } catch {
      toast.error('An error occurred. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <CardTitle>Unable to Load Preferences</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">{message}</p>
            <div className="flex flex-col gap-2 pt-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Log in to manage preferences
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Mail className="h-8 w-8 text-primary" />
              <div>
                <CardTitle>Email Preferences</CardTitle>
                <CardDescription>Choose which emails you'd like to receive</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Transactional notice */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-1">Essential notifications</p>
              <p className="text-muted-foreground">
                Account security, password resets, and billing notifications are always sent and
                cannot be disabled.
              </p>
            </div>

            <Separator />

            {/* Product & Service emails */}
            <div className="space-y-4">
              <h3 className="font-medium">Product & Service</h3>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="product_updates">Product updates</Label>
                  <p className="text-sm text-muted-foreground">
                    New features, improvements, and platform news
                  </p>
                </div>
                <Switch
                  id="product_updates"
                  checked={preferences?.product_updates}
                  onCheckedChange={() => handleToggle('product_updates')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="feedback_requests">Feedback requests</Label>
                  <p className="text-sm text-muted-foreground">
                    Surveys and feedback opportunities to help improve EcoSemantic
                  </p>
                </div>
                <Switch
                  id="feedback_requests"
                  checked={preferences?.feedback_requests}
                  onCheckedChange={() => handleToggle('feedback_requests')}
                />
              </div>
            </div>

            <Separator />

            {/* Marketing emails */}
            <div className="space-y-4">
              <h3 className="font-medium">Marketing & Promotions</h3>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="promotions">Promotions & offers</Label>
                  <p className="text-sm text-muted-foreground">
                    Special offers, discounts, and promotional codes
                  </p>
                </div>
                <Switch
                  id="promotions"
                  checked={preferences?.promotions}
                  onCheckedChange={() => handleToggle('promotions')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="newsletter">Newsletter</Label>
                  <p className="text-sm text-muted-foreground">
                    Monthly digest with LCA insights and sustainability news
                  </p>
                </div>
                <Switch
                  id="newsletter"
                  checked={preferences?.newsletter}
                  onCheckedChange={() => handleToggle('newsletter')}
                />
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button onClick={handleSave} disabled={!hasChanges || saving} className="w-full">
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Preferences
              </Button>

              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
